import { defineStore } from 'pinia';
import _ from 'lodash';
import {
  Queue,
  QueueAttributes,
  CreateQueue,
  UpdateQueue,
  GetList,
  GetAttributes,
  DeleteQueue,
  GetMetricData,
  MetricDataForCharts,
} from '@/types';
import QueuesApi from '@/api/QueuesApi';
import { useDateFormat } from '@vueuse/core';
import { AxiosError } from '@or-sdk/base';
import { TranslatableError } from '@/errors';


export const useQueuesStore = defineStore('queues', {
  state: () => ({
    queues: [] as Queue[],
    next: '',
    queueAttributes: {} as QueueAttributes,
    monitoringData: [] as MetricDataForCharts[],
    uniqueName: true,
    queueByName: {} as Queue | undefined,
  }),
  getters: {},
  actions: {
    async getList(payload: GetList): Promise<void> {
      const response = await QueuesApi.listQueues(
        { amount: payload.amount },
        { next: payload.next },
      );

      this.queues = _.get(response, 'body.records');
      this.next = _.get(response, 'body.next');
    },

    async scrollQueues(payload: GetList): Promise<void> {
      const response = await QueuesApi.scrollQueues(payload).catch(error => {
        if (error instanceof AxiosError && error.response?.data.message.includes('Minimal role to access is user')) {
          throw new TranslatableError('errors.minimumAccessIsUser', error);
        }
        throw error;
      });
      this.queues = _.get(response, 'records');
      this.next = _.get(response, 'next') || 'end';
    },

    async createQueue(payload: CreateQueue): Promise<void> {
      await QueuesApi.createQueue(payload);
    },

    async updateQueue(payload: UpdateQueue): Promise<void> {
      await QueuesApi.updateQueue(payload);
    },

    async getAttributes(payload: GetAttributes): Promise<void> {
      const response = await QueuesApi.getAttributes(payload);
      const maxSize = `${Number(_.get(response, 'MaximumMessageSize')) / 1024} kB`;
      this.queueAttributes = response;
      _.set(this.queueAttributes, 'MaximumMessageSize', maxSize);
    },

    async deleteQueue(payload: DeleteQueue): Promise<void> {
      await QueuesApi.deleteQueue(payload);
    },

    async checkName(queueName: string): Promise<void> {
      const response = await QueuesApi.checkQueueName({ queueName });
      if (response) {this.uniqueName = false;}
      else {this.uniqueName = true;}
    },

    async getQueueByName(queueName: string): Promise<void> {
      const response = await QueuesApi.checkQueueName({ queueName });
      if (response) {
        this.queueByName = _.get(response, 'body');
      }
    },

    async getMetricData(payload: GetMetricData): Promise<void> {
      const response = await QueuesApi.getMetricData(payload);
      const data = _.flatten(response.map(el => el.values.map((value, index) => {
        const date = useDateFormat(el.timestamps[index], 'MM/DD HH:mm');
        return {
          label: date.value,
          series: el.label,
          value: Math.round(value),
        };
      })));
      this.monitoringData = _.sortBy(data, ['label']);
    },
  },
});

export default useQueuesStore;
